// SurfaceVistas — small low-poly city/landmark slices on the planet.
// These are not literal map pins. They are recognizable silhouettes
// that let the camera move from orbit toward a lived-in surface.

import { useMemo, useRef, type ReactNode } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

type VistaKind = 'paris' | 'tokyo' | 'kuala-lumpur' | 'sydney';

interface VistaSpec {
  id: string;
  sceneId: number;
  kind: VistaKind;
  position: [number, number, number];
  scale: number;
  yaw: number;
  label: string;
  description: string;
}

const VISTAS: VistaSpec[] = [
  {
    id: 'paris-tower',
    sceneId: 1,
    kind: 'paris',
    position: [2.18, 0.52, 1.02],
    scale: 0.42,
    yaw: -0.35,
    label: 'Paris signal',
    description: 'A warm tower signal on the first inhabited edge of the world.',
  },
  {
    id: 'tokyo-fuji',
    sceneId: 2,
    kind: 'tokyo',
    position: [-1.42, 1.36, 2.08],
    scale: 0.46,
    yaw: 0.45,
    label: 'Tokyo ridge',
    description: 'A compressed skyline, a mountain silhouette, and a line of quiet work.',
  },
  {
    id: 'kuala-lumpur-towers',
    sceneId: 3,
    kind: 'kuala-lumpur',
    position: [0.9, -0.9, 2.48],
    scale: 0.48,
    yaw: -0.1,
    label: 'Kuala Lumpur relay',
    description: 'Two towers carry the projects chapter back into orbit.',
  },
  {
    id: 'sydney-harbor',
    sceneId: 4,
    kind: 'sydney',
    position: [-1.86, 1.9, -0.92],
    scale: 0.42,
    yaw: 0.2,
    label: 'Sydney harbor',
    description: 'A small harbor for fragments, field notes, and unfinished ideas.',
  },
];

const LOCAL_UP = new THREE.Vector3(0, 1, 0);
const MINT = '#00e5ff';
const GOLD = '#ffed00';
const WARM = '#fff3df';
const STONE = '#ff5a36';
const DARK_GLASS = '#151d78';
const SIGNAL_LAYOUT: Array<[number, number, number, number]> = [
  [-0.46, -0.22, 0.1, 0.24],
  [-0.3, 0.31, -0.08, 0.36],
  [-0.08, -0.38, 0.03, 0.18],
  [0.12, 0.27, 0.08, 0.3],
  [0.33, -0.21, -0.06, 0.28],
  [0.46, 0.15, 0.03, 0.18],
];

export interface SurfaceVistasProps {
  activeSceneIndex: number;
}

function useSurfaceTransform(position: [number, number, number], yaw: number) {
  return useMemo(() => {
    const outward = new THREE.Vector3(...position).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(LOCAL_UP, outward);
    const yawQuat = new THREE.Quaternion().setFromAxisAngle(LOCAL_UP, yaw);
    quat.multiply(yawQuat);
    const euler = new THREE.Euler().setFromQuaternion(quat);
    return {
      rotation: [euler.x, euler.y, euler.z] as [number, number, number],
    };
  }, [position, yaw]);
}

function VistaShell({
  spec,
  active,
  children,
}: {
  spec: VistaSpec;
  active: boolean;
  children: ReactNode;
}) {
  const { rotation } = useSurfaceTransform(spec.position, spec.yaw);
  const shellRef = useRef<THREE.Group | null>(null);
  const signalRef = useRef<THREE.Group | null>(null);
  const pulseRef = useRef<THREE.Mesh | null>(null);
  const visibilityScale = spec.scale * (active ? 1.05 : 0.78);
  const baseOpacity = active ? 0.52 : 0.28;

  useFrame((state, delta) => {
    const shell = shellRef.current;
    if (!shell) return;
    const reduced = typeof window !== 'undefined' && window.__planetReducedMotion === true;
    const targetScale = spec.scale * (active ? 1.1 : 0.8);
    const damp = 1 - Math.exp(-delta * 5);
    shell.scale.setScalar(THREE.MathUtils.lerp(shell.scale.x, targetScale, damp));

    if (reduced) return;
    const time = state.clock.elapsedTime;
    if (signalRef.current) {
      signalRef.current.rotation.y += delta * (active ? 0.18 : 0.035);
      signalRef.current.position.y = Math.sin(time * 1.4 + spec.sceneId) * (active ? 0.024 : 0.006);
    }
    if (pulseRef.current) {
      const pulse = 1 + (Math.sin(time * 2.4 + spec.sceneId) + 1) * (active ? 0.08 : 0.025);
      pulseRef.current.scale.setScalar(pulse);
      const material = pulseRef.current.material as THREE.MeshBasicMaterial;
      material.opacity = active ? 0.3 + (Math.sin(time * 2.4) + 1) * 0.1 : 0.08;
    }
  });

  const openVista = (event: { stopPropagation: () => void }) => {
    if (typeof window === 'undefined' || window.__planetMode !== 'exploration') return;
    event.stopPropagation();
    const point = new THREE.Vector3();
    shellRef.current?.getWorldPosition(point);
    window.__planetShowInfoCard?.({
      kind: 'vista',
      id: spec.id,
      title: spec.label,
      blurb: spec.description,
      worldPos: [point.x, point.y, point.z],
    });
  };

  return (
    <group
      ref={shellRef}
      name={`surface-vista-${spec.id}`}
      position={spec.position}
      rotation={rotation}
      scale={visibilityScale}
      raycast={() => null}
      userData={{ surfaceVista: spec.id }}
    >
      <mesh
        name={`${spec.id}-plate`}
        position={[0, 0.012, 0]}
        onClick={openVista}
        onPointerOver={() => {
          if (typeof window !== 'undefined' && window.__planetMode === 'exploration') {
            document.body.style.cursor = 'pointer';
          }
        }}
        onPointerOut={() => {
          document.body.style.cursor = '';
        }}
      >
        <cylinderGeometry args={[0.72, 0.82, 0.035, 7]} />
        <meshStandardMaterial
          color={DARK_GLASS}
          transparent
          opacity={baseOpacity}
          roughness={0.82}
          metalness={0.04}
          flatShading
          depthWrite={false}
        />
      </mesh>
      <mesh name={`${spec.id}-rim`} position={[0, 0.037, 0]} raycast={() => null}>
        <torusGeometry args={[0.74, 0.008, 5, 72]} />
        <meshBasicMaterial
          color={active ? MINT : GOLD}
          transparent
          opacity={active ? 0.42 : 0.18}
          depthWrite={false}
        />
      </mesh>
      <group ref={signalRef}>
        <mesh
          ref={pulseRef}
          name={`${spec.id}-pulse`}
          position={[0, 0.052, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          raycast={() => null}
        >
          <ringGeometry args={[0.24, 0.258, 32]} />
          <meshBasicMaterial
            color={active ? MINT : GOLD}
            transparent
            opacity={active ? 0.32 : 0.08}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
        {SIGNAL_LAYOUT.map(([x, z, phase, height], index) => (
          <mesh
            key={`${spec.id}-signal-${index}`}
            position={[x, 0.054 + height / 2, z]}
            rotation={[0, phase, 0]}
            raycast={() => null}
          >
            <boxGeometry args={[0.026, height, 0.026]} />
            <meshStandardMaterial
              color={index % 2 === 0 ? MINT : GOLD}
              emissive={index % 2 === 0 ? MINT : GOLD}
              emissiveIntensity={active ? 0.62 : 0.14}
              roughness={0.48}
              transparent
              opacity={active ? 0.82 : 0.3}
            />
          </mesh>
        ))}
        {children}
      </group>
    </group>
  );
}

function ParisTower({ active }: { active: boolean }) {
  const glow = active ? 0.3 : 0.12;
  return (
    <group name="vista-paris-tower">
      {[[-0.22, -0.16], [0.22, -0.16], [-0.14, 0.18], [0.14, 0.18]].map(([x, z], i) => (
        <mesh
          key={`leg-${i}`}
          position={[x, 0.34, z]}
          rotation={[z > 0 ? -0.14 : 0.14, 0, x > 0 ? -0.22 : 0.22]}
          raycast={() => null}
        >
          <cylinderGeometry args={[0.026, 0.05, 0.72, 4]} />
          <meshStandardMaterial
            color={GOLD}
            emissive={GOLD}
            emissiveIntensity={glow}
            roughness={0.7}
            flatShading
          />
        </mesh>
      ))}
      <mesh position={[0, 0.72, 0]} raycast={() => null}>
        <boxGeometry args={[0.42, 0.045, 0.32]} />
        <meshStandardMaterial color={WARM} roughness={0.65} flatShading />
      </mesh>
      <mesh position={[0, 0.95, 0]} raycast={() => null}>
        <cylinderGeometry args={[0.03, 0.12, 0.5, 4]} />
        <meshStandardMaterial
          color={GOLD}
          emissive={GOLD}
          emissiveIntensity={glow}
          roughness={0.65}
          flatShading
        />
      </mesh>
      <mesh position={[0, 1.28, 0]} raycast={() => null}>
        <coneGeometry args={[0.045, 0.34, 4]} />
        <meshStandardMaterial color={MINT} emissive={MINT} emissiveIntensity={glow} flatShading />
      </mesh>
    </group>
  );
}

function TokyoFuji({ active }: { active: boolean }) {
  const glow = active ? 0.26 : 0.1;
  return (
    <group name="vista-tokyo-fuji">
      <group position={[-0.16, 0, 0.05]}>
        <mesh position={[-0.18, 0.34, 0]} raycast={() => null}>
          <cylinderGeometry args={[0.035, 0.045, 0.66, 5]} />
          <meshStandardMaterial color={GOLD} roughness={0.72} flatShading />
        </mesh>
        <mesh position={[0.18, 0.34, 0]} raycast={() => null}>
          <cylinderGeometry args={[0.035, 0.045, 0.66, 5]} />
          <meshStandardMaterial color={GOLD} roughness={0.72} flatShading />
        </mesh>
        <mesh position={[0, 0.64, 0]} raycast={() => null}>
          <boxGeometry args={[0.52, 0.07, 0.12]} />
          <meshStandardMaterial
            color={WARM}
            emissive={WARM}
            emissiveIntensity={glow}
            roughness={0.6}
            flatShading
          />
        </mesh>
        <mesh position={[0, 0.78, 0]} raycast={() => null}>
          <boxGeometry args={[0.68, 0.08, 0.14]} />
          <meshStandardMaterial color={MINT} emissive={MINT} emissiveIntensity={glow} flatShading />
        </mesh>
      </group>
      <mesh position={[0.24, 0.28, -0.18]} rotation={[0, 0.25, 0]} raycast={() => null}>
        <coneGeometry args={[0.34, 0.56, 6]} />
        <meshStandardMaterial color={STONE} roughness={0.9} flatShading />
      </mesh>
      <mesh position={[0.24, 0.58, -0.18]} rotation={[0, 0.25, 0]} raycast={() => null}>
        <coneGeometry args={[0.13, 0.18, 6]} />
        <meshStandardMaterial color={WARM} roughness={0.7} flatShading />
      </mesh>
    </group>
  );
}

function KualaLumpurTowers({ active }: { active: boolean }) {
  const glow = active ? 0.34 : 0.12;
  return (
    <group name="vista-kuala-lumpur-towers">
      {[-0.18, 0.18].map((x) => (
        <group key={x} position={[x, 0, 0]}>
          <mesh position={[0, 0.45, 0]} raycast={() => null}>
            <cylinderGeometry args={[0.08, 0.11, 0.9, 8]} />
            <meshStandardMaterial color={STONE} roughness={0.72} metalness={0.08} flatShading />
          </mesh>
          <mesh position={[0, 0.94, 0]} raycast={() => null}>
            <coneGeometry args={[0.075, 0.22, 8]} />
            <meshStandardMaterial
              color={GOLD}
              emissive={GOLD}
              emissiveIntensity={glow}
              roughness={0.6}
              flatShading
            />
          </mesh>
          <mesh position={[0, 1.1, 0]} raycast={() => null}>
            <cylinderGeometry args={[0.014, 0.02, 0.28, 5]} />
            <meshStandardMaterial color={MINT} emissive={MINT} emissiveIntensity={glow} flatShading />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 0.58, 0]} raycast={() => null}>
        <boxGeometry args={[0.36, 0.055, 0.08]} />
        <meshStandardMaterial color={WARM} roughness={0.55} metalness={0.04} flatShading />
      </mesh>
      <mesh position={[0, 0.37, 0.14]} raycast={() => null}>
        <boxGeometry args={[0.62, 0.18, 0.18]} />
        <meshStandardMaterial color={DARK_GLASS} roughness={0.85} flatShading />
      </mesh>
    </group>
  );
}

function SydneyHarbor({ active }: { active: boolean }) {
  const glow = active ? 0.28 : 0.1;
  return (
    <group name="vista-sydney-harbor">
      <mesh position={[0, 0.07, -0.04]} raycast={() => null}>
        <boxGeometry args={[0.82, 0.09, 0.24]} />
        <meshStandardMaterial color={STONE} roughness={0.82} flatShading />
      </mesh>
      {[-0.28, -0.08, 0.12, 0.32].map((x, i) => (
        <mesh
          key={x}
          position={[x, 0.25 + i * 0.025, 0.02]}
          rotation={[0, 0, -0.58 + i * 0.13]}
          scale={[0.78, 1.15, 0.32]}
          raycast={() => null}
        >
          <coneGeometry args={[0.16, 0.54, 4]} />
          <meshStandardMaterial
            color={WARM}
            emissive={WARM}
            emissiveIntensity={glow}
            roughness={0.62}
            flatShading
          />
        </mesh>
      ))}
      <mesh position={[0, 0.11, 0.28]} rotation={[Math.PI / 2, 0, 0]} raycast={() => null}>
        <torusGeometry args={[0.48, 0.012, 5, 64, Math.PI]} />
        <meshBasicMaterial color={MINT} transparent opacity={active ? 0.52 : 0.24} />
      </mesh>
    </group>
  );
}

function renderVista(kind: VistaKind, active: boolean) {
  switch (kind) {
    case 'paris':
      return <ParisTower active={active} />;
    case 'tokyo':
      return <TokyoFuji active={active} />;
    case 'kuala-lumpur':
      return <KualaLumpurTowers active={active} />;
    case 'sydney':
      return <SydneyHarbor active={active} />;
    default:
      return null;
  }
}

export function SurfaceVistas({ activeSceneIndex }: SurfaceVistasProps) {
  return (
    <group name="surface-vistas" userData={{ pickable: false }}>
      {VISTAS.map((spec) => {
        const active = spec.sceneId === activeSceneIndex;
        return (
          <VistaShell key={spec.id} spec={spec} active={active}>
            {renderVista(spec.kind, active)}
          </VistaShell>
        );
      })}
    </group>
  );
}

export default SurfaceVistas;
